// STATIC VISUAL PREVIEW of the "Ring Center" shell laid over the Avida app.
// Layout: Avida side nav (collapsible) + slim top bar + three near-equal
// columns (list · conversation · context). No live data, no modules wired.
// Temporary public route for envisioning only; folded into the authed app in Phase 2.
import React, { useState } from "react";
import {
  Search, Plus, Bell, Inbox, Phone, MessageSquare, Video, Users, Printer,
  PhoneMissed, Voicemail, PhoneIncoming, Send, Paperclip, Star, MoreHorizontal,
  PanelRightClose, PanelRightOpen, LayoutDashboard, CalendarDays, Briefcase,
  Settings, ChevronLeft, Menu,
  MicOff, LayoutGrid, Volume2, Pause, PhoneForwarded, UserPlus, Circle, PhoneOff, X,
  Mail, MapPin, FileText, Megaphone,
} from "lucide-react";

// ── Reconciled tokens ──────────────────────────────────────────────
const NAVY = "#032866";
const NAVY_DEEP = "#002244";
const GOLD = "#FFDA00";
const MUTED = "#7B8FA3";
const HAIRLINE = "rgba(3,40,102,0.08)";
const NAVY_TINT = "rgba(3,40,102,0.06)";
const ROYAL_BLUE = "#1668DA";
const ROYAL = "#2A7CC4";
const RED = "#C0392B";
const AMBER = "#BA7517";
const GREEN = "#1D9E75";
const FONT_HEAD = "'Manrope', sans-serif";
const FONT_BODY = "'IBM Plex Sans', sans-serif";

const SECTIONS = [
  { id: "inbox", label: "Inbox", icon: Inbox },
  { id: "phone", label: "Phone", icon: Phone },
  { id: "text", label: "Text", icon: MessageSquare },
  { id: "blast", label: "Blast", icon: Megaphone },
  { id: "chat", label: "Chat", icon: MessageSquare },
  { id: "meet", label: "Meet", icon: Video },
  { id: "contacts", label: "Contacts", icon: Users },
  { id: "fax", label: "Fax", icon: Printer },
];

const SUBNAV = ["All", "Calls", "Texts", "Voicemail", "Unassigned"];

const ROWS = [
  { name: "Eleanor Whitfield", snippet: "Missed call · no voicemail", time: "9:41a", accent: RED, icon: PhoneMissed, badge: "1", unread: true },
  { name: "Gerald Chen", snippet: "New voicemail · “Hi, calling about my visit schedule…”", time: "9:12a", accent: ROYAL, icon: Voicemail, badge: "0:42", unread: true },
  { name: "(414) 555-0173", snippet: "Text: running about 10 minutes late", time: "8:55a", accent: AMBER, icon: MessageSquare, badge: "2", unread: true },
  { name: "Ruth Kaminski", snippet: "Inbound call · 4m 18s", time: "Yest", accent: GREEN, icon: PhoneIncoming, badge: "", unread: false },
  { name: "James Osei", snippet: "Text blast delivered · Reminder", time: "Yest", accent: "transparent", icon: MessageSquare, badge: "", handled: true },
  { name: "Lorraine Peters", snippet: "Call returned · handled", time: "Mon", accent: "transparent", icon: Phone, badge: "", handled: true },
];

const AVIDA_NAV = [
  { icon: LayoutDashboard, label: "Dashboard" },
  { icon: Phone, label: "Phone", active: true },
  { icon: MessageSquare, label: "Team Chat" },
  { icon: Video, label: "Meetings" },
  { icon: Printer, label: "Fax" },
  { icon: Users, label: "Contacts" },
  { icon: CalendarDays, label: "Calendar" },
  { icon: Briefcase, label: "Work" },
  { icon: Settings, label: "Settings" },
];

function AvidaSidebar({ open, setOpen }) {
  return (
    <div style={{ width: open ? 216 : 60, background: NAVY, fontFamily: FONT_BODY }} className="shrink-0 flex flex-col transition-all duration-200" data-testid="rc-avida-nav">
      <div className="flex items-center h-[52px] px-3" style={{ borderBottom: "1px solid rgba(255,255,255,0.10)" }}>
        {open && <span style={{ color: GOLD, fontFamily: FONT_HEAD }} className="font-extrabold tracking-wide text-lg">AVIDA</span>}
        <button data-testid="rc-nav-collapse" onClick={() => setOpen(!open)} className="ml-auto" style={{ color: "rgba(255,255,255,0.75)" }}>
          {open ? <ChevronLeft size={18} /> : <Menu size={18} />}
        </button>
      </div>
      <div className="flex-1 overflow-y-auto py-2">
        {AVIDA_NAV.map((it) => {
          const I = it.icon;
          return (
            <div
              key={it.label}
              title={it.label}
              className="flex items-center gap-3 mx-2 my-0.5 px-2.5 h-10 rounded-lg cursor-pointer"
              style={{ background: it.active ? "rgba(255,218,0,0.16)" : "transparent", color: it.active ? GOLD : "rgba(255,255,255,0.85)" }}
            >
              <I size={18} strokeWidth={1.9} className="shrink-0" />
              {open && <span className="text-[13px] font-medium truncate">{it.label}</span>}
            </div>
          );
        })}
      </div>
    </div>
  );
}

function TopBar({ view, callOpen, onSelect }) {
  return (
    <div data-testid="rc-topbar" style={{ height: 64, background: ROYAL_BLUE, borderBottom: "1px solid rgba(255,255,255,0.15)", fontFamily: FONT_BODY }} className="shrink-0 flex items-center gap-4 px-4">
      <nav className="flex items-center gap-1 h-full">
        {SECTIONS.map((s) => {
          const active = s.id === "phone" ? callOpen : s.id === view;
          const Icon = s.icon;
          const clickable = s.id === "inbox" || s.id === "phone";
          return (
            <div
              key={s.id}
              data-testid={`rc-nav-${s.id}`}
              onClick={() => clickable && onSelect(s.id)}
              className="relative h-full flex items-center gap-2 px-4 cursor-pointer text-[14px] font-semibold"
              style={{ color: active ? GOLD : "rgba(255,255,255,0.92)" }}
            >
              <Icon size={19} strokeWidth={2.6} color={GOLD} />
              {s.label}
              {active && <span style={{ background: GOLD, height: 3 }} className="absolute left-3 right-3 bottom-0 rounded-full" />}
            </div>
          );
        })}
      </nav>
      <div className="ml-auto flex items-center gap-2.5">
        <div className="flex items-center gap-2 rounded-md px-2.5 h-9 w-48" style={{ border: "1px solid rgba(255,255,255,0.28)" }}>
          <Search size={15} color={GOLD} /><span className="text-[12px] truncate" style={{ color: "rgba(255,255,255,0.85)" }}>Search…</span>
        </div>
        <div className="flex items-center gap-1.5 rounded-full pl-1.5 pr-2.5 h-8 text-[12px] font-medium" style={{ border: "1px solid rgba(255,255,255,0.28)", color: "#fff" }}>
          <span style={{ background: GREEN }} className="w-2 h-2 rounded-full" /> Available
        </div>
        <button data-testid="rc-new" style={{ background: GOLD, color: NAVY_DEEP }} className="flex items-center gap-1 rounded-md h-9 px-3.5 text-[12px] font-bold"><Plus size={15} /> New</button>
        <div className="relative"><Bell size={19} color={GOLD} strokeWidth={1.9} /><span style={{ background: RED }} className="absolute -top-1 -right-1 w-3.5 h-3.5 rounded-full text-white text-[8px] font-bold grid place-items-center">3</span></div>
        <div style={{ background: "#fff", color: ROYAL_BLUE }} className="w-9 h-9 rounded-full grid place-items-center text-[12px] font-bold">DA</div>
      </div>
    </div>
  );
}

function ListRail({ phone }) {
  return (
    <div data-testid="rc-list-rail" style={{ borderRight: `1px solid ${HAIRLINE}`, fontFamily: FONT_BODY }} className="flex-1 min-w-0 flex flex-col bg-white">
      <div className="flex items-center gap-1 px-3 h-11 shrink-0 overflow-x-auto" style={{ background: GOLD, borderBottom: `1px solid ${HAIRLINE}` }}>
        {(phone ? ["Recents", "Missed", "Voicemail", "Contacts"] : SUBNAV).map((t, i) => (
          <span key={t} className="text-[12px] font-bold px-2.5 py-1 rounded-md shrink-0" style={i === 0 ? { background: NAVY_DEEP, color: GOLD } : { color: NAVY_DEEP }}>{t}</span>
        ))}
      </div>
      <div className="overflow-y-auto flex-1">
        {ROWS.map((r, i) => {
          const Icon = r.icon;
          return (
            <div key={i} className="flex items-start gap-3 px-3 py-3 cursor-pointer" style={{ minHeight: 56, borderBottom: `1px solid ${HAIRLINE}`, borderLeft: `4px solid ${r.accent}`, opacity: r.handled ? 0.72 : 1, background: i === 1 ? "rgba(3,40,102,0.04)" : "transparent" }}>
              <div className="mt-0.5" style={{ color: r.accent === "transparent" ? MUTED : r.accent }}><Icon size={16} strokeWidth={1.9} /></div>
              <div className="min-w-0 flex-1">
                <div className="flex items-center justify-between gap-2">
                  <span className="text-[13px] font-semibold truncate" style={{ color: NAVY_DEEP, fontFamily: FONT_HEAD }}>{r.name}</span>
                  <span className="text-[11px] shrink-0" style={{ color: MUTED }}>{r.time}</span>
                </div>
                <div className="text-[12px] leading-snug line-clamp-2" style={{ color: r.unread ? NAVY_DEEP : MUTED, fontWeight: r.unread ? 500 : 400 }}>{r.snippet}</div>
              </div>
              {r.badge ? <span className="shrink-0 text-[10px] font-bold rounded-full px-1.5 py-0.5 mt-0.5" style={{ background: r.accent === "transparent" ? MUTED : r.accent, color: "#fff" }}>{r.badge}</span> : null}
            </div>
          );
        })}
      </div>
    </div>
  );
}

function ThreadPane({ ctxOpen, setCtxOpen }) {
  return (
    <div data-testid="rc-thread" className="flex-1 min-w-0 flex flex-col bg-white" style={{ fontFamily: FONT_BODY }}>
      <div className="flex items-center gap-3 px-4 h-14 shrink-0" style={{ borderBottom: `1px solid ${HAIRLINE}` }}>
        <div className="w-9 h-9 rounded-full grid place-items-center text-white text-[12px] font-bold" style={{ background: ROYAL }}>GC</div>
        <div className="min-w-0">
          <div className="text-[14px] font-bold" style={{ color: NAVY_DEEP, fontFamily: FONT_HEAD }}>Gerald Chen</div>
          <div className="text-[11px] truncate" style={{ color: MUTED }}>(414) 555-0142 · Active auth</div>
        </div>
        <div className="ml-auto flex items-center gap-2.5" style={{ color: NAVY }}>
          <Phone size={16} strokeWidth={1.9} /><Star size={16} strokeWidth={1.9} />
          <button data-testid="rc-ctx-toggle" onClick={() => setCtxOpen(!ctxOpen)} title="Toggle context panel">{ctxOpen ? <PanelRightClose size={17} strokeWidth={1.9} /> : <PanelRightOpen size={17} strokeWidth={1.9} />}</button>
          <MoreHorizontal size={17} strokeWidth={1.9} />
        </div>
      </div>
      <div className="flex-1 overflow-y-auto p-4 space-y-3" style={{ background: "#F8FAFC" }}>
        <div className="text-center text-[11px]" style={{ color: MUTED }}>Today</div>
        <div className="flex"><div className="max-w-[82%] rounded-2xl rounded-tl-sm px-3.5 py-2 text-[13px] bg-white" style={{ border: `1px solid ${HAIRLINE}`, color: NAVY_DEEP }}>Voicemail transcript: “Hi, this is Gerald, just calling about my visit schedule for next week…”</div></div>
        <div className="flex justify-end"><div className="max-w-[82%] rounded-2xl rounded-tr-sm px-3.5 py-2 text-[13px] text-white" style={{ background: NAVY }}>Hi Gerald — you're set for Tue &amp; Thu at 9am. Want a reminder text the night before?</div></div>
        <div className="flex justify-end"><div className="max-w-[82%] rounded-2xl rounded-tr-sm px-3.5 py-2 text-[13px] text-white" style={{ background: NAVY }}>Sent via SMS ✓</div></div>
      </div>
      <div className="shrink-0 px-4 pt-3 pb-4" style={{ borderTop: `1px solid ${HAIRLINE}`, background: "#fff" }}>
        <div className="flex flex-col rounded-xl overflow-hidden" style={{ minHeight: 180, border: `1px solid ${HAIRLINE}` }}>
          <div className="flex-1 px-4 pt-3 text-[13px]" style={{ color: MUTED }}>Type a message…</div>
          <div className="flex items-center justify-between px-3 py-2" style={{ borderTop: `1px solid ${HAIRLINE}` }}>
            <div className="flex items-center gap-3" style={{ color: MUTED }}><Paperclip size={17} /><MessageSquare size={17} /><Phone size={17} /></div>
            <button style={{ background: GOLD, color: NAVY_DEEP }} className="flex items-center gap-1.5 rounded-lg h-8 px-4 text-[13px] font-bold"><Send size={14} /> Send</button>
          </div>
        </div>
      </div>
    </div>
  );
}

function FloatingSoftphone({ onClose }) {
  const controls = [
    { icon: MicOff, label: "Mute" },
    { icon: LayoutGrid, label: "Keypad" },
    { icon: Volume2, label: "Speaker" },
    { icon: Pause, label: "Hold", hot: true },
    { icon: PhoneForwarded, label: "Transfer", hot: true },
    { icon: UserPlus, label: "Add call" },
    { icon: Circle, label: "Record" },
    { icon: Video, label: "Video" },
    { icon: Users, label: "Merge" },
  ];
  return (
    <div data-testid="rc-softphone" className="rounded-2xl bg-white shadow-2xl flex flex-col items-center px-7 py-5" style={{ border: `1px solid ${HAIRLINE}`, width: 300, fontFamily: FONT_BODY }}>
      <div className="w-full flex items-center mb-3">
        <span className="text-[11px] font-bold uppercase tracking-widest" style={{ color: MUTED }}>Active call</span>
        <button data-testid="rc-softphone-close" onClick={onClose} className="ml-auto" style={{ color: MUTED }}><X size={16} /></button>
      </div>
      <div className="w-14 h-14 rounded-full grid place-items-center text-white text-base font-bold mb-2" style={{ background: ROYAL }}>GC</div>
      <div className="text-[19px] font-semibold" style={{ color: NAVY_DEEP, fontFamily: FONT_HEAD }}>Gerald Chen</div>
      <div className="text-[12px]" style={{ color: MUTED }}>(414) 555-0142</div>
      <div className="flex items-center gap-1.5 mt-1 mb-5 text-[12px] font-medium" style={{ color: GREEN }}>
        <span className="w-2 h-2 rounded-full" style={{ background: GREEN }} /> In call · 02:14
      </div>
      <div className="grid grid-cols-3 gap-x-6 gap-y-4 mb-6">
        {controls.map((c) => {
          const I = c.icon;
          return (
            <div key={c.label} className="flex flex-col items-center gap-1.5" data-testid={`rc-call-${c.label.toLowerCase().replace(/\s/g, "-")}`}>
              <div className="w-13 h-13 rounded-full grid place-items-center" style={{ width: 52, height: 52, border: `1px solid ${c.hot ? NAVY : HAIRLINE}`, background: c.hot ? "rgba(3,40,102,0.06)" : "#fff", color: c.hot ? NAVY : NAVY_DEEP }}>
                <I size={19} strokeWidth={1.9} />
              </div>
              <span className="text-[11px] font-medium" style={{ color: c.hot ? NAVY : MUTED }}>{c.label}</span>
            </div>
          );
        })}
      </div>
      <button data-testid="rc-call-end" className="w-14 h-14 rounded-full grid place-items-center text-white" style={{ background: RED }}><PhoneOff size={22} /></button>
    </div>
  );
}

function Fact({ k, v, warn }) {
  return (
    <div className="py-0.5 min-w-0">
      <div className="text-[10px] leading-tight" style={{ color: MUTED }}>{k}</div>
      <div className="text-[12px] font-semibold leading-tight truncate" style={{ color: warn ? AMBER : NAVY_DEEP }}>{v}</div>
    </div>
  );
}

function Facts({ items }) {
  return (
    <div className="grid grid-cols-2 gap-x-3 gap-y-1.5">
      {items.map((it) => <Fact key={it.k} {...it} />)}
    </div>
  );
}

function Section({ title, children }) {
  return (
    <div className="px-4 pt-2.5 pb-2" style={{ borderTop: `1px solid ${HAIRLINE}` }}>
      <div className="text-[10px] font-bold uppercase tracking-widest mb-1.5" style={{ color: MUTED }}>{title}</div>
      {children}
    </div>
  );
}

function ContextPanel() {
  return (
    <div data-testid="rc-context" style={{ fontFamily: FONT_BODY }} className="flex-1 min-w-0 bg-white overflow-y-auto">
      <div className="px-4 pt-4 pb-3 flex flex-col items-center text-center">
        <div className="w-14 h-14 rounded-full grid place-items-center text-white text-lg font-bold" style={{ background: ROYAL }}>GC</div>
        <div className="mt-2 text-[15px] font-bold" style={{ color: NAVY_DEEP, fontFamily: FONT_HEAD }}>Gerald Chen</div>
        <div className="text-[12px]" style={{ color: MUTED }}>Client · Milwaukee, WI · Age 74</div>
        <div className="flex flex-wrap gap-1.5 justify-center mt-2">
          <span className="text-[10px] font-semibold px-2 py-0.5 rounded-full" style={{ background: "rgba(29,158,117,0.12)", color: GREEN }}>Active auth</span>
          <span className="text-[10px] font-semibold px-2 py-0.5 rounded-full" style={{ background: "rgba(192,57,43,0.10)", color: RED }}>Callback due</span>
          <span className="text-[10px] font-semibold px-2 py-0.5 rounded-full" style={{ background: NAVY_TINT, color: NAVY }}>Text preferred</span>
        </div>
        <div className="flex gap-2 mt-3">
          {[Phone, MessageSquare, Video, FileText].map((I, i) => (<div key={i} className="w-9 h-9 rounded-full grid place-items-center cursor-pointer" style={{ border: `1px solid ${HAIRLINE}`, color: NAVY }}><I size={16} strokeWidth={1.9} /></div>))}
        </div>
      </div>

      <Section title="Authorized units">
        <div className="flex items-baseline justify-between">
          <span className="text-[19px] font-black" style={{ color: NAVY_DEEP, fontFamily: FONT_HEAD }}>118<span className="text-[12px] font-medium" style={{ color: MUTED }}> / 180 hrs</span></span>
          <span className="text-[11px] font-semibold" style={{ color: GREEN }}>66% left</span>
        </div>
        <div className="h-2 rounded-full mt-1.5 overflow-hidden" style={{ background: NAVY_TINT }}>
          <div style={{ width: "66%", background: GREEN, height: "100%" }} />
        </div>
      </Section>

      <Section title="Authorization & payer">
        <Facts items={[
          { k: "Client #", v: "AV-100482" },
          { k: "Medicaid ID", v: "1234-567-890" },
          { k: "Payer / MCO", v: "iCare (Humana)" },
          { k: "Auth period", v: "Jun 1 – Dec 1" },
          { k: "PA expires", v: "in 66 days", warn: true },
        ]} />
      </Section>

      <Section title="Care & schedule">
        <Facts items={[
          { k: "PCW", v: "Terrance B." },
          { k: "Next visit", v: "Tue 9:00a" },
          { k: "Supervisory due", v: "in 12 days", warn: true },
        ]} />
      </Section>

      <Section title="Contact">
        <div className="flex items-center gap-2 py-1 text-[12px]" style={{ color: NAVY_DEEP }}><Phone size={13} color={MUTED} /> (414) 555-0142 <span style={{ color: MUTED }}>· mobile</span></div>
        <div className="flex items-center gap-2 py-1 text-[12px]" style={{ color: NAVY_DEEP }}><Mail size={13} color={MUTED} /> gerald.chen@email.com</div>
        <div className="flex items-center gap-2 py-1 text-[12px]" style={{ color: NAVY_DEEP }}><MapPin size={13} color={MUTED} /> 1420 N 5th St, Milwaukee</div>
      </Section>

      <Section title="Recent history">
        {[
          { t: "Outbound SMS · reminder sent", d: "9:14a", c: NAVY },
          { t: "Voicemail · 0:42", d: "9:12a", c: ROYAL },
          { t: "Missed call", d: "Mon", c: RED },
          { t: "Inbound call · 4m 18s", d: "Sun", c: GREEN },
        ].map((h, i) => (
          <div key={i} className="flex items-center gap-2 py-1.5 text-[12px]">
            <span className="w-2 h-2 rounded-full shrink-0" style={{ background: h.c }} />
            <span className="flex-1" style={{ color: NAVY_DEEP }}>{h.t}</span><span style={{ color: MUTED }}>{h.d}</span>
          </div>
        ))}
      </Section>

      <Section title="Latest note">
        <div className="text-[12px] leading-snug p-2.5 rounded-lg mb-3" style={{ background: NAVY_TINT, color: NAVY_DEEP }}>“Client prefers morning visits; daughter (POA) cc’d on scheduling. Confirmed Tue/Thu 9am.” — Dawn, 9:14a</div>
      </Section>
    </div>
  );
}

export default function RingCenterPreview() {
  const [navOpen, setNavOpen] = useState(true);
  const [ctxOpen, setCtxOpen] = useState(true);
  const [callOpen, setCallOpen] = useState(false);
  const [view, setView] = useState("inbox");
  const onSelect = (id) => { if (id === "phone") setCallOpen(true); else setView(id); };
  return (
    <div data-testid="ring-center-preview" className="h-screen w-full flex flex-col overflow-hidden" style={{ background: "#fff" }}>
      <div className="text-[11px] px-4 py-1 text-center shrink-0" style={{ background: GOLD, color: NAVY_DEEP, fontFamily: FONT_BODY }}>
        Static preview — no live data. The dialer is a floating softphone (green button, bottom-right, or the “Phone” tab) that opens OVER your screen — it never replaces SMS.
      </div>
      <div className="flex-1 flex min-h-0 overflow-hidden">
        <AvidaSidebar open={navOpen} setOpen={setNavOpen} />
        <div className="flex-1 flex flex-col min-w-0">
          <TopBar view={view} callOpen={callOpen} onSelect={onSelect} />
          <div className="flex-1 flex min-h-0 overflow-hidden">
            <ListRail />
            <div className="flex-1 min-w-0 relative flex">
              <ThreadPane ctxOpen={ctxOpen} setCtxOpen={setCtxOpen} />
              {callOpen && (
                <div className="absolute left-1/2 bottom-6 -translate-x-1/2 z-40">
                  <FloatingSoftphone onClose={() => setCallOpen(false)} />
                </div>
              )}
            </div>
            {ctxOpen && <ContextPanel />}
          </div>
        </div>
      </div>
      {!callOpen && (
        <button data-testid="rc-softphone-fab" onClick={() => setCallOpen(true)} className="fixed bottom-6 right-6 w-14 h-14 rounded-full grid place-items-center text-white shadow-2xl z-50" style={{ background: GREEN }}>
          <Phone size={22} />
        </button>
      )}
    </div>
  );
}
